import { ZonedDateTime } from '@internationalized/date';
import {
  TruckSortOption,
  TruckStatus,
} from '@rematter/frontend-lib-api-client';

import { TruckRecord } from '~/modules/transportation/types';
import { createQueryHook } from '~/shared/providers/api';
import {
  maybeParseCalendarDate,
  maybeParseZonedDateTime,
} from '~/shared/utils/dateTime';

export const useTrucksQuery = createQueryHook(
  'transportation.trucks',
  (params: {
    // Filters
    ids?: string[];
    status?: TruckStatus;
    licensePlate?: string;
    model?: string;
    minCapacity?: number;
    maxCapacity?: number;
    lastUpdatedAt?: ZonedDateTime;
    searchTerm?: string;
    limit?: number;
    offset?: number;
    sorting?: TruckSortOption[];

    // Query params
    includeDefaultTrailer?: boolean;
  }) => ({
    trucks: {
      __args: {
        filter: {
          ids: params.ids,
          status: params.status ? [params.status] : undefined,
          licensePlate: params.licensePlate,
          model: params.model,
          minCapacity: params.minCapacity,
          maxCapacity: params.maxCapacity,
          lastUpdatedAt: params.lastUpdatedAt?.toAbsoluteString(),
          searchTerm: params.searchTerm,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 10,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        status: true,
        currentLocation: {
          latitude: true,
          longitude: true,
        },
        lastLocationTime: true,
        licensePlate: true,
        licensePlateExpiration: true,
        vin: true,
        model: true,
        capacity: true,
        country: true,
        state: true,
        notes: true,
        transportationType: {
          id: true,
          name: true,
        },
        facility: {
          id: true,
        },
        defaultTrailer: params.includeDefaultTrailer
          ? {
              id: true,
              licensePlate: true,
            }
          : undefined,
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (data): { trucks: TruckRecord[]; total?: number } => ({
      trucks: data?.trucks.entries.map(
        (truck) =>
          ({
            id: truck.id,
            status: truck.status,
            licensePlate: truck.licensePlate,
            licensePlateExpiration: maybeParseCalendarDate(
              truck.licensePlateExpiration,
            ),
            model: truck.model,
            transportationType: truck.transportationType,
            capacity: truck.capacity,
            location: truck.currentLocation,
            country: truck.country,
            state: truck.state,
            vin: truck.vin,
            notes: truck.notes,
            facilityId: truck.facility.id,
            defaultTrailer: truck.defaultTrailer
              ? {
                  id: truck.defaultTrailer.id,
                  licensePlate: truck.defaultTrailer.licensePlate,
                }
              : undefined,
            timestamps: {
              createdAt: maybeParseZonedDateTime(truck.timestamps.createdAt),
              updatedAt: maybeParseZonedDateTime(truck.timestamps.updatedAt),
            },
          }) as TruckRecord,
      ),
      total: data?.trucks.page.total ?? undefined,
    }),
  },
);
